import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Subscription, take, takeUntil } from 'rxjs';
import { MarkupService } from 'src/app/markup-table/markup.service';
import { ClientMarkup } from 'src/app/markup-table/model/markup.model';
import { NotificationService } from 'src/app/services/notification.service';
import { Client } from 'src/typings';
import { ClientMarkupCreateComponent } from './client-markup-create.component';

@Component({
  selector: 'app-client-markup-table',
  templateUrl: './client-markup-table.component.html',
  styleUrls: ['./client-markup-table.component.scss']
})
export class ClientMarkupTableComponent implements OnChanges, OnDestroy {
  constructor(
    private dialog: MatDialog,
    private markupService: MarkupService,
    private notificationService: NotificationService
  ) {
    this.subscriptions.push(
      this.markupService.clientCreateEvent.subscribe((event) => this.reloadIfCurrentClient(event?.clientId)),
      this.markupService.clientUpdatedEvent.subscribe((event) => this.reloadIfCurrentClient(event?.clientId)),
      this.markupService.clientDeleteEvent.subscribe((event) => this.reloadIfCurrentClient(event?.clientId)),
      this.markupService.clientTemplateAppliedEvent.subscribe((event) =>
        this.reloadIfCurrentClient(event?.clientId)
      )
    );
  }

  @Input() client?: Client;
  @Input() layout: 'table' | 'sidebar' = 'sidebar';

  loading = false;
  applyingTemplate = false;
  error = '';
  private revision = 0;

  dataSource: ClientMarkup[] = [];
  templateOptions = [
    { label: '0%', delta: 0 },
    { label: '-5%', delta: -5 },
    { label: '-10%', delta: -10 },
    { label: '-15%', delta: -15 },
    { label: '+5%', delta: 5 },
    { label: '+10%', delta: 10 }
  ];

  private subscriptions: Subscription[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes.client?.currentValue?.id && changes.client?.previousValue?.id !== this.client?.id) {
      this.load();
    }
  }

  ngOnDestroy() {
    this.revision++;
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  async load() {
    if (!this.client?.id) return;
    this.loading = true; this.error = '';
    const revision = ++this.revision;
    try {
      const rows = await firstValueFrom(
        this.markupService.searchClient(this.client.id, { order: 'ASC', orderBy: 'id' })
      );
      if (revision === this.revision) this.dataSource = rows;
    } catch { if (revision === this.revision) this.error = 'Не удалось загрузить наценку клиента';
    } finally {
      if (revision === this.revision) this.loading = false;
    }
  }

  add() {
    if (!this.client?.id) return;
    const dialog = this.dialog.open(ClientMarkupCreateComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false
    });
    dialog.componentInstance.clientId = this.client.id;
    const subs = dialog.componentInstance.doneEvent.pipe(take(1), takeUntil(dialog.afterClosed())).subscribe(() => {
      subs.unsubscribe();
      dialog.close();
      this.load();
      this.notificationService.notice('Наценка клиента сохранена');
    });
  }

  update(markup: ClientMarkup) {
    if (!this.client?.id) return;
    const dialog = this.dialog.open(ClientMarkupCreateComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false
    });
    dialog.componentInstance.clientId = this.client.id;
    dialog.componentInstance.markup = markup;
    const subs = dialog.componentInstance.doneEvent.pipe(take(1), takeUntil(dialog.afterClosed())).subscribe(() => {
      subs.unsubscribe();
      dialog.close();
      this.load();
      this.notificationService.notice('Наценка клиента сохранена');
    });
  }

  async remove(markup: ClientMarkup) {
    if (!this.client?.id || this.loading) return;
    this.loading = true;
    try {
      await firstValueFrom(this.markupService.deleteClient(this.client.id, markup.id));
      await this.load();
    } finally {
      this.loading = false;
    }
  }

  async applyTemplate(deltaPercent: number) {
    if (!this.client?.id || this.loading || this.applyingTemplate) return;
    const templateLabel = deltaPercent > 0 ? `+${deltaPercent}%` : `${deltaPercent}%`;
    const floorNote = deltaPercent === -15 ? ' Минимальная процентная наценка будет 5%.' : '';
    const confirmed = globalThis.confirm(
      `Применить шаблон ${templateLabel}?${floorNote} Будут заменены базовые и предыдущие шаблонные правила. Ручные правила с папкой или брендом останутся.`
    );
    if (!confirmed) return;

    this.applyingTemplate = true;
    try {
      await firstValueFrom(
        this.markupService.applyClientTemplate(this.client.id, { deltaPercent })
      );
      await this.load();
      this.notificationService.notice('Шаблон наценки применен');
    } finally {
      this.applyingTemplate = false;
    }
  }

  async applyHistoryTemplate() {
    if (!this.client?.id || this.loading || this.applyingTemplate) return;
    const confirmed = globalThis.confirm(
      `Применить шаблон История 12м для клиента #${this.client.id}? Будут использованы закрытые оплаченные и полностью отгруженные заказы за последние 12 месяцев. Диапазоны без достаточной истории будут взяты из глобальной таблицы. Ручные правила с папкой или брендом останутся.`
    );
    if (!confirmed) return;

    this.applyingTemplate = true;
    try {
      await firstValueFrom(this.markupService.applyClientHistoryTemplate(this.client.id));
      await this.load();
      this.notificationService.notice('Исторический шаблон наценки применен');
    } finally {
      this.applyingTemplate = false;
    }
  }

  get isTableLayout() {
    return this.layout === 'table';
  }

  private reloadIfCurrentClient(clientId?: number) {
    if (clientId && this.client?.id === clientId) this.load();
  }
}
